import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

// M06-03 (UI-Audit C5 / §19) — Draft Management: Save / Resume / Delete / Submit.
// Backend: T079 drafts CRUD (POST/PUT/DELETE/GET /request/drafts[/:id][/submit]).
export interface DraftRow {
  readonly draftId: string;
  readonly customerName: string | null;
  readonly lastSavedAt: string;
  readonly saveCount: number;
  readonly surveyData: { industry?: string; brandingType?: string; siteCity?: string } | null;
}

export function SalesDraftsPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<DraftRow[]>('/v1/job-requests/drafts', { token });
      setRows(r);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  function resume(draftId: string) {
    navigate(`/sales/jobs/new?draftId=${encodeURIComponent(draftId)}`);
  }

  async function remove(draftId: string) {
    setBusyId(draftId); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/job-requests/drafts/${draftId}`, { method: 'DELETE', token });
      setInfo(t('sales.drafts.deleted'));
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusyId(null); }
  }

  async function submitDraft(draftId: string) {
    setBusyId(draftId); setError(null); setInfo(null);
    try {
      const res = await apiRequest<{ id?: string; jobRequestId?: string }>(
        `/v1/job-requests/drafts/${draftId}/submit`,
        { method: 'POST', token, body: {} },
      );
      const jobId = res.jobRequestId ?? res.id;
      if (jobId) { navigate(`/sales/jobs/${jobId}`); return; }
      setInfo(t('sales.drafts.submitted'));
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusyId(null); }
  }

  return (
    <main aria-label="sales-drafts">
      <h1>{t('sales.drafts.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {info ? <p role="status" aria-label="drafts-info">{info}</p> : null}
      {!rows ? <p>{t('sales.drafts.loading')}</p> : rows.length === 0 ? <p aria-label="no-drafts">{t('sales.drafts.empty')}</p> : (
        <table aria-label="drafts-table">
          <thead><tr><th>{t('sales.drafts.col.summary')}</th><th>{t('sales.drafts.col.saved')}</th><th>{t('sales.drafts.col.saves')}</th><th /></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.draftId} aria-label={`draft-row-${d.draftId}`}>
                <td>{d.customerName ?? d.surveyData?.industry ?? t('sales.drafts.untitled')}{d.surveyData?.brandingType ? ` · ${d.surveyData.brandingType}` : ''}{d.surveyData?.siteCity ? ` · ${d.surveyData.siteCity}` : ''}</td>
                <td>{at(d.lastSavedAt)}</td>
                <td>{d.saveCount}</td>
                <td>
                  <button type="button" aria-label={`resume-${d.draftId}`} onClick={() => resume(d.draftId)} disabled={busyId === d.draftId}>{t('sales.drafts.resume')}</button>
                  <button type="button" aria-label={`submit-${d.draftId}`} onClick={() => { void submitDraft(d.draftId); }} disabled={busyId === d.draftId}>{t('sales.drafts.submit')}</button>
                  <button type="button" aria-label={`delete-${d.draftId}`} onClick={() => { void remove(d.draftId); }} disabled={busyId === d.draftId}>{t('sales.drafts.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
